import { isEmpty, splitArray, unique } from '@terros/common'

/**
 * Defines what an empty selection means and whether selecting every option is stored
 * as an empty array or as explicit IDs. `allowEmpty` controls whether "none" is valid.
 */
export type MultiSelectFilterBehavior =
  | {
      emptyBehavior: 'all'
      allValueBehavior: 'empty' | 'explicit'
    }
  | {
      emptyBehavior: 'none'
      allowEmpty: boolean
    }

export type MultiSelectFilterRowAction = 'check' | 'selectAll' | 'selectOnly'

export type MultiSelectFilterStateInput<T extends string> = {
  allValues: T[]
  selectedValues: T[]
  behavior: MultiSelectFilterBehavior
}

type MultiSelectFilterValueInput<T extends string> = MultiSelectFilterStateInput<T> & {
  value: T
}

type NormalizedValues<T extends string> = {
  selectedValues: T[]
  inScopeValues: T[]
  preservedValues: T[]
}

const normalizeValues = <T extends string>({
  allValues,
  selectedValues,
}: MultiSelectFilterStateInput<T>): NormalizedValues<T> => {
  const normalizedSelectedValues = unique(selectedValues)
  const allValuesSet = new Set(allValues)
  const { yes: inScopeValues, no: preservedValues } = splitArray(normalizedSelectedValues, (value) =>
    allValuesSet.has(value)
  )
  return { selectedValues: normalizedSelectedValues, inScopeValues, preservedValues }
}

const toInScopeSelection = <T extends string>(input: MultiSelectFilterStateInput<T>): T[] => {
  if (input.behavior.emptyBehavior === 'all' && isEmpty(input.selectedValues)) return input.allValues
  return normalizeValues(input).inScopeValues
}

export const isMultiSelectFilterEmptySelectionAll = (behavior: MultiSelectFilterBehavior): boolean =>
  behavior.emptyBehavior === 'all'

export const isMultiSelectFilterAllSelected = <T extends string>({
  allValues,
  selectedValues,
  behavior,
}: MultiSelectFilterStateInput<T>): boolean => {
  if (isEmpty(allValues)) return false
  if (behavior.emptyBehavior === 'all' && isEmpty(selectedValues)) return true
  const { inScopeValues } = normalizeValues({ allValues, selectedValues, behavior })
  return allValues.every((availableValue) => inScopeValues.includes(availableValue))
}

export const isMultiSelectFilterValueSelected = <T extends string>({
  allValues,
  selectedValues,
  behavior,
  value,
}: MultiSelectFilterValueInput<T>): boolean => {
  if (!allValues.includes(value)) return selectedValues.includes(value)
  if (behavior.emptyBehavior === 'all' && isEmpty(selectedValues)) return true
  const { inScopeValues } = normalizeValues({ allValues, selectedValues, behavior })
  return inScopeValues.includes(value)
}

export const getMultiSelectFilterSelectedCount = <T extends string>(input: MultiSelectFilterStateInput<T>): number => {
  if (isMultiSelectFilterAllSelected(input)) return input.allValues.length
  return normalizeValues(input).inScopeValues.length
}

export const getMultiSelectFilterRowAction = ({
  isSelected,
  isAllSelected,
  selectedCount,
}: {
  isSelected: boolean
  isAllSelected: boolean
  selectedCount: number
}): MultiSelectFilterRowAction => {
  if (isEmpty(selectedCount)) return 'check'
  if (isSelected && !isAllSelected) return 'selectAll'
  return 'selectOnly'
}

const toBehaviorValue = <T extends string>({
  allValues,
  selectedValues,
  behavior,
}: MultiSelectFilterStateInput<T>): T[] => {
  const { inScopeValues, preservedValues } = normalizeValues({ allValues, selectedValues, behavior })

  if (behavior.emptyBehavior === 'none') {
    if (!behavior.allowEmpty && isEmpty(selectedValues)) return allValues
    return unique(selectedValues)
  }

  const representsAll =
    isEmpty(selectedValues) || allValues.every((availableValue) => inScopeValues.includes(availableValue))
  if (!representsAll) return unique(selectedValues)
  if (behavior.allValueBehavior === 'empty') return []
  return [...preservedValues, ...allValues]
}

export const toggleMultiSelectFilterValue = <T extends string>(input: MultiSelectFilterValueInput<T>): T[] => {
  const { allValues, selectedValues, behavior, value } = input
  const { preservedValues } = normalizeValues(input)

  if (!allValues.includes(value)) {
    const nextValues = selectedValues.includes(value)
      ? unique(selectedValues).filter((selectedValue) => selectedValue !== value)
      : [...unique(selectedValues), value]
    return toBehaviorValue({ allValues, selectedValues: nextValues, behavior })
  }

  const selectedInScopeValues = toInScopeSelection(input)
  const nextInScopeValues = selectedInScopeValues.includes(value)
    ? selectedInScopeValues.filter((selectedValue) => selectedValue !== value)
    : [...selectedInScopeValues, value]

  return toBehaviorValue({ allValues, selectedValues: [...preservedValues, ...nextInScopeValues], behavior })
}

export const selectMultiSelectFilterRow = <T extends string>(input: MultiSelectFilterValueInput<T>): T[] => {
  const { allValues, behavior, value } = input
  const isAllSelected = isMultiSelectFilterAllSelected(input)
  const isSelected = isMultiSelectFilterValueSelected(input)
  const selectedCount = getMultiSelectFilterSelectedCount(input)
  const { preservedValues } = normalizeValues(input)

  if (isSelected && !isAllSelected)
    return toBehaviorValue({ allValues, selectedValues: [...preservedValues, ...allValues], behavior })
  if (isEmpty(selectedCount))
    return toBehaviorValue({ allValues, selectedValues: [...preservedValues, value], behavior })
  return toBehaviorValue({ allValues, selectedValues: [value], behavior })
}

export const doesMultiSelectFilterToggleSelectAll = <T extends string>(
  input: MultiSelectFilterValueInput<T>
): boolean => {
  const nextSelectedValues = toggleMultiSelectFilterValue(input)
  return isMultiSelectFilterAllSelected({ ...input, selectedValues: nextSelectedValues })
}

export const getMultiSelectFilterAllValue = <T extends string>({
  allValues,
  behavior,
}: Pick<MultiSelectFilterStateInput<T>, 'allValues' | 'behavior'>): T[] =>
  toBehaviorValue({ allValues, selectedValues: allValues, behavior })

export type MultiSelectFilterGroupInput<T extends string> = MultiSelectFilterStateInput<T> & {
  groupValues: T[]
}

export const toggleMultiSelectFilterGroup = <T extends string>({
  allValues,
  selectedValues,
  behavior,
  groupValues,
  isGroupSelected,
}: MultiSelectFilterGroupInput<T> & { isGroupSelected: boolean }): T[] => {
  const state: MultiSelectFilterStateInput<T> = { allValues, selectedValues, behavior }
  const { preservedValues } = normalizeValues(state)
  const groupValueSet = new Set(groupValues)
  const currentValues = [...preservedValues, ...toInScopeSelection(state)]

  const nextValues = isGroupSelected
    ? currentValues.filter((selectedValue) => !groupValueSet.has(selectedValue))
    : [...currentValues, ...groupValues]

  return toBehaviorValue({ allValues, selectedValues: nextValues, behavior })
}

export const doesToggleMultiSelectFilterGroupSelectAll = <T extends string>(
  input: MultiSelectFilterGroupInput<T> & { isGroupSelected: boolean }
): boolean => {
  const nextSelectedValues = toggleMultiSelectFilterGroup(input)
  return isMultiSelectFilterAllSelected({ ...input, selectedValues: nextSelectedValues })
}

export const selectMultiSelectFilterGroupOnly = <T extends string>({
  allValues,
  behavior,
  groupValues,
}: MultiSelectFilterGroupInput<T>): T[] => toBehaviorValue({ allValues, selectedValues: groupValues, behavior })
